<script setup lang="ts">
/**
 * 源站 .tabbar_container：Menú / Casino / Depositar / Actividad / Perfil。
 * 450 宽实测：高 74.4 / bg #111214 / 每项 90×73.4 / 字 14.4 / 选中 #EE1E40。
 */
import { computed } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { t } from '@/shared/i18n/messages';
import { oroMxAssetPaths } from '@/shared/theme/oro-mx-home-data';
import { useAuthModalStore } from '@/stores/auth-modal-store';
import { useOroMxShellStore } from '@/stores/oro-mx-shell-store';
import { requestGuestBindPrompt } from '@/features/auth/guest-bind-prompt';
import { useSessionStore } from '@/stores/session-store';

const route = useRoute();
const router = useRouter();
const shell = useOroMxShellStore();
const sessionStore = useSessionStore();
const authModal = useAuthModalStore();
const sprite = oroMxAssetPaths.sprite;

const items = computed(() => [
  { name: 'home', label: t('oro.tab.casino'), to: '/', symbol: 'icon-casino' },
  { name: 'deposit', label: t('oro.tab.deposit'), to: '/deposit', symbol: 'icon-tabbar-wallet', wide: true },
  { name: 'promotions', label: t('oro.tab.activity'), to: '/promotions', symbol: 'icon-gift' },
]);

const activeName = computed(() => {
  if (shell.menuOpen) return 'menu';
  const name = String(route.name ?? '');
  if (name === 'home') return 'home';
  if (name === 'deposit' || name === 'withdraw' || name === 'wallet') return 'deposit';
  if (
    name === 'promotions' ||
    name === 'promotionDetail' ||
    name === 'activityHallDetail' ||
    name === 'activities' ||
    name === 'bonus'
  ) {
    return 'promotions';
  }
  if (name === 'account' || name === 'profile' || name === 'profileEdit' || name === 'withdrawAccounts') return 'account';
  return '';
});

function onNavClick() {
  shell.closeMenu();
}

/** 源站 Perfil：未登录停在当前页并弹登录；已登录进「我的」。未绑手机则每次点击都再打开绑定弹窗。 */
function onAccountClick() {
  shell.closeMenu();
  if (!sessionStore.token) sessionStore.restoreSession();
  if (!sessionStore.isAuthenticated) {
    authModal.openAuth({ mode: 'login' });
    return;
  }
  requestGuestBindPrompt();
  if (route.name !== 'account') {
    void router.push('/account');
  }
}
</script>

<template>
  <nav class="tabbar_container" :aria-label="t('oro.nav.aria')">
    <button
      class="tabbar_items"
      :class="{ 'tabbar_items--active': activeName === 'menu' }"
      type="button"
      @click="shell.toggleMenu()"
    >
      <svg class="tabbar_icon" viewBox="0 0 48 48" aria-hidden="true">
        <use :href="`${sprite}#icon-menu`" />
      </svg>
      <span class="tabbar_label">{{ t('oro.tab.menu') }}</span>
    </button>
    <RouterLink
      v-for="item in items"
      :key="item.name"
      class="tabbar_items"
      :class="{ 'tabbar_items--active': activeName === item.name }"
      :to="item.to ?? '/'"
      @click="onNavClick"
    >
      <svg
        class="tabbar_icon"
        :class="{ 'tabbar_icon--wide': item.wide }"
        :viewBox="item.wide ? '0 0 71 71' : '0 0 48 48'"
        aria-hidden="true"
      >
        <use :href="`${sprite}#${item.symbol}`" />
      </svg>
      <span class="tabbar_label">{{ item.label }}</span>
    </RouterLink>
    <button
      class="tabbar_items"
      :class="{ 'tabbar_items--active': activeName === 'account' }"
      type="button"
      @click="onAccountClick"
    >
      <svg class="tabbar_icon" viewBox="0 0 48 48" aria-hidden="true">
        <use :href="`${sprite}#icon-games`" />
      </svg>
      <span class="tabbar_label">{{ t('oro.tab.profile') }}</span>
    </button>
  </nav>
</template>

<style scoped>
.tabbar_container {
  z-index: 3200;
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 3.3067rem;
  background: #111214;
  border-top: 1px solid #000;
}

.tabbar_items {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.32rem;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #fff;
  text-decoration: none;
  background: transparent;
  border: 0;
}

.tabbar_items--active {
  color: #ee1e40;
}

.tabbar_icon {
  display: block;
  width: 1.0667rem;
  height: 1.0667rem;
  fill: currentColor;
}

.tabbar_icon--wide {
  width: 1.7778rem;
  height: 1.7778rem;
  fill: none;
}

.tabbar_label {
  font-size: 0.64rem;
  font-weight: 400;
  line-height: 0.96rem;
}
</style>
